import {
  ProfileHero,
  ProfileHeroBadge,
  type ProfileHeroStat,
} from '@/components/common/ProfileHero';
import { ProfileLogo } from '@/components/common/ProfileLogo';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { FUNDING_STAGE_OPTIONS } from '@/lib/constants/funding-stages';
import { contactTitleLabel } from '@/lib/constants/contact-titles';
import { formatCompactCurrencyRange, joinTruthy, displayUrl } from '@/lib/utils/format.utils';
import type { InvestorProfileData } from '../../api/investor-profile.api';
import {
  LEAD_OR_FOLLOW_OPTIONS,
  REVENUE_OPTIONS,
  investorTypeLabel,
  labelFor,
} from './investor-options';
import { INVESTOR_PROFILE_HERO_STRINGS as H } from './investor-form.constants';

const S = UI_STRINGS.INVESTOR_PROFILE;

function InvestorAvatar({ data, name }: { data: InvestorProfileData; name: string }) {
  return (
    <div className="relative">
      <ProfileLogo url={data.logoUrl} name={name} ring="dark" tone="teal" />
      {(data.profilePhotoUrl || data.contactName) && (
        <span className="absolute -right-2.5 -bottom-2.5">
          <ProfileLogo
            url={data.profilePhotoUrl}
            name={data.contactName ?? name}
            size={44}
            shape="circle"
            ring="navy"
          />
        </span>
      )}
    </div>
  );
}

function investorBadges(data: InvestorProfileData) {
  return (
    <>
      {data.activelyDeploying && (
        <ProfileHeroBadge tone="green">{H.ACTIVELY_DEPLOYING}</ProfileHeroBadge>
      )}
      {data.isPrivate !== false && (
        <ProfileHeroBadge tone="muted">{S.PRIVATE_BADGE}</ProfileHeroBadge>
      )}
    </>
  );
}

function investorMeta(data: InvestorProfileData) {
  const contact = joinTruthy([data.contactName, contactTitleLabel(data.contactTitle)], ', ');
  const location = joinTruthy([data.addressCity, data.addressState]);
  return (
    <>
      {contact && <span>{contact}</span>}
      {data.investorType && <span>{investorTypeLabel(data.investorType)}</span>}
      {location && <span>{location}</span>}
      {data.websiteUrl && (
        <a
          href={data.websiteUrl}
          target="_blank"
          rel="noreferrer"
          className="text-green hover:underline"
        >
          {displayUrl(data.websiteUrl)}
        </a>
      )}
    </>
  );
}

function toAmount(value: string | number | null | undefined): number | undefined {
  return value == null || value === '' ? undefined : Number(value);
}

/** First and last preferred stage, e.g. "Pre-seed - Series A", or the one stage. */
function stageSpan(stages: readonly string[] | undefined): string | undefined {
  const labels = (stages ?? []).map((stage) => labelFor(FUNDING_STAGE_OPTIONS, stage) ?? stage);
  if (labels.length <= 1) return labels[0];
  return `${labels[0]} - ${labels[labels.length - 1]}`;
}

function investorStats(data: InvestorProfileData): ProfileHeroStat[] {
  return [
    {
      label: H.TICKET_SIZE,
      value: formatCompactCurrencyRange(
        toAmount(data.prefAmountLow),
        toAmount(data.prefAmountHigh),
      ),
    },
    { label: H.STAGES, value: stageSpan(data.preferredStages) },
    { label: H.LEAD_OR_FOLLOW, value: labelFor(LEAD_OR_FOLLOW_OPTIONS, data.prefLeadOrFollow) },
    { label: H.MIN_REVENUE, value: labelFor(REVENUE_OPTIONS, data.prefMinRevenue) },
  ];
}

/**
 * The header's title and the small line above it (FR-187). The firm name is the
 * entity name the person was invited under; the fund name is the profile's own.
 * Fund name empty or equal to the firm name -> the firm name once, as the title.
 * Never a placeholder: a profile always has an entity name.
 */
function heroNames(data: InvestorProfileData): { name: string; eyebrow?: string } {
  const firm = data.entityName?.trim() ?? '';
  const fund = data.firmName?.trim() ?? '';
  const sameName = fund.toLowerCase() === firm.toLowerCase();
  if (!fund || sameName) return { name: firm || fund };
  return { name: fund, eyebrow: firm || undefined };
}

/**
 * Investor profile header (FR-174, FR-187): firm logo with the contact photo,
 * the firm name above the fund name, badges, and the ticket / stages /
 * lead-or-follow / minimum-revenue strip. No completion %: investor has no
 * completion score (A-156).
 */
export function InvestorProfileHero({ data }: { data: InvestorProfileData }) {
  const { name, eyebrow } = heroNames(data);
  return (
    <ProfileHero
      avatar={<InvestorAvatar data={data} name={name} />}
      name={name}
      eyebrow={eyebrow}
      badges={investorBadges(data)}
      description={data.intro ?? undefined}
      meta={investorMeta(data)}
      stats={investorStats(data)}
    />
  );
}
